import { useEventCreateEventMilestone, useEventDeleteEventMilestone } from "@crowdcoursing/api/openapi";
import { useCallback } from "react";

import { type Milestone } from "~ui/molecules/TasksMilestonesCard/TasksMilestonesCard";

import { useToast } from "./useToast";

export interface NewMilestoneItem {
  name: string;
  description: string;
  dueDate: Date;
}

export const useEventMilestoneManagement = (eventId: string, onSuccess?: () => void) => {
  const toast = useToast();

  const { mutateAsync: createMilestoneMutation, isPending: isCreating } = useEventCreateEventMilestone();
  const { mutateAsync: deleteMilestoneMutation, isPending: isDeleting } = useEventDeleteEventMilestone();

  const createMilestone = useCallback(
    async (milestone: NewMilestoneItem) => {
      await createMilestoneMutation(
        {
          body: {
            description: milestone.description,
            dueDate: milestone.dueDate.toJSON(),
            eventId,
            name: milestone.name,
          },
          pathParams: { eventId },
        },
        {
          onError: (error) => {
            console.error("Create milestone error:", error);
            toast.error("Failed to create milestone");
          },
          onSuccess: () => {
            toast.success("Milestone created successfully!");
            onSuccess?.();
          },
        },
      );
    },
    [createMilestoneMutation, eventId, toast, onSuccess],
  );

  const deleteMilestone = useCallback(
    async (milestone: Milestone) => {
      await deleteMilestoneMutation(
        {
          pathParams: {
            eventId,
            milestoneId: milestone.id,
          },
        },
        {
          onError: (error) => {
            console.error("Delete milestone error:", error);
            toast.error("Failed to delete milestone");
          },
          onSuccess: () => {
            toast.success("Milestone deleted successfully!");
            onSuccess?.();
          },
        },
      );
    },
    [deleteMilestoneMutation, eventId, toast, onSuccess],
  );

  const completeMilestone = useCallback(
    async (milestone: Milestone) => {
      await deleteMilestoneMutation(
        {
          pathParams: {
            eventId,
            milestoneId: milestone.id,
          },
        },
        {
          onError: (error) => {
            console.error("Complete milestone error:", error);
            toast.error("Failed to complete milestone");
          },
          onSuccess: () => {
            toast.success("Milestone completed!");
            onSuccess?.();
          },
        },
      );
    },
    [deleteMilestoneMutation, eventId, toast, onSuccess],
  );

  return {
    completeMilestone,
    createMilestone,
    deleteMilestone,
    isCreating,
    isDeleting,
    isLoading: isCreating || isDeleting,
  };
};
